import {
  settingsWords as words,
  settingsText,
} from "@/components/settings/settings-copy";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { integrationWords } from "@/components/settings/integrations-copy";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useWorkspaceEyebrow } from "@/hooks/use-workspace-eyebrow";
import { useRecordingSigninBox } from "@/components/settings/recording-signin-box";
import { useZoomReceiverBoxes } from "@/components/settings/zoom-receiver-box";
import { useZoomVerificationBox } from "@/components/settings/zoom-verification-box";
import { useGradeScaleBox } from "@/components/settings/grade-scale-box";
import { AcademicPageHeader } from "@/components/records/AcademicWorkspace";
import { useTabDraft } from "@/components/ui/box-draft";
import { Bento, PageRefusal } from "@/components/ui/box";
import type { Box, Blocker, Conflict } from "@/components/ui/box.schema";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { ApiError } from "@/lib/api/client";
import { currentOnConflict } from "@/lib/api/conflict";
import {
  settingsApi,
  type ConfigurableMeetingMethod,
  type CourseDeliveryConflict,
  type InstitutionSettings,
} from "@/lib/api/settings";
import {
  boxRead,
  imam,
  useSettingsCommit,
  type PendingSettingsSave,
} from "@/components/settings/settings-boxes";
import {
  settingsCopy as copy,
  deliveryOf,
  sameDelivery,
  deliveryConflictFacts,
  describeChange,
  type SettingsChange,

} from "@/components/settings/settings-copy";

const METHODS = copy.METHODS;
const LABEL = copy.method;

export function SettingsPage() {
  const eyebrow = useWorkspaceEyebrow();
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const gradeScale = useGradeScaleBox();
  const settings = useQuery({
    queryKey: ["admin", "settings"],
    queryFn: settingsApi.get,
    retry: false,
  });
  const snapshot = settings.data;
  const ranking = useQuery({
    queryKey: ["admin", "settings-ranking"],
    queryFn: settingsApi.ranking,
    retry: false,
  });
  const initial = {
    enabled: snapshot?.course_delivery.enabled_meeting_methods ?? [],
    defaultMethod: snapshot?.course_delivery.default_meeting_method ?? "in_person" as ConfigurableMeetingMethod,
    locationsText: snapshot?.course_delivery.locations.join("\n") ?? "",
    reasonText: "",
  };
  const keep = useTabDraft<typeof initial, PendingSettingsSave>({
    box: "course-delivery", record: "institution",
    version: snapshot ? String(snapshot.settings_version) : null, initial,
  });
  const { enabled, defaultMethod, locationsText, reasonText } = keep.value;
  const setEnabled = (next: ConfigurableMeetingMethod[]) => keep.set((was) => ({ ...was, enabled: next }));
  const setDefaultMethod = (next: ConfigurableMeetingMethod) => keep.set((was) => ({ ...was, defaultMethod: next }));
  const setLocationsText = (next: string) => keep.set((was) => ({ ...was, locationsText: next }));
  const setReasonText = (next: string) => keep.set((was) => ({ ...was, reasonText: next }));
  const [neededVersion, setNeededVersion] = useState<number | null>(null);
  const [serverConflicted, setConflicted] = useState(false);
  const refresh = async () => { await settings.refetch(); };
  const outcome = useSettingsCommit({ keep, refresh, versionField: "settings_version" });
  const locations = useMemo(
    () => locationsText.split("\n").map((value) => value.trim()).filter(Boolean),
    [locationsText],
  );
  const entry = { enabled, defaultMethod, locations };
  const saved = snapshot ? deliveryOf(snapshot) : null;
  const edited = saved !== null && !sameDelivery(entry, saved);
  const expectedVersion = keep.began === null ? snapshot?.settings_version : Number(keep.began);
  const conflicted = !keep.pending && (serverConflicted || Boolean(keep.conflict));
  const read = keep.settled ? boxRead(settings, copy.deliveryUnavailable) : { status: "loading" as const };
  const changed = (part: string) => {
    if (!saved) return false;
    if (part === "locations")
      return locations.join("\n") !== saved.locations.join("\n");
    if (part === "default") return defaultMethod !== saved.defaultMethod;
    return enabled.join(",") !== saved.enabled.join(",");
  };
  const changedParts = ["methods", "default", "locations"].filter(changed);
  const conflictPart = changed("locations")
    ? "locations"
    : changed("default") ? "default" : "methods";
  const zoomEnabled = Boolean(
    snapshot?.provider_administration.zoom_recording_receiver,
  );
  const signinRank =
    ranking.data?.state === "ready"
      ? (ranking.data.value?.candidates.find(
          (candidate) => candidate.id === "recording-signin",
        )?.rank ?? null)
      : null;
  const signinBox = useRecordingSigninBox(read, signinRank);
  const receiverBoxes = useZoomReceiverBoxes(zoomEnabled);
  const verificationBox = useZoomVerificationBox(zoomEnabled);
  const change = (run: () => void) => {
    outcome.reset();
    run();
  };
  const invalid =
    locations.length > 50 || locations.some((value) => value.length > 200);
  const conflict: Conflict | undefined =
    conflicted &&
    snapshot &&
    (keep.conflict || (neededVersion !== null && snapshot.settings_version >= neededVersion))
      ? {
          sentence: settingsText.deliveryConflict(changedParts),
          facts: (deliveryConflictFacts(deliveryOf(snapshot), entry).length
            ? deliveryConflictFacts(deliveryOf(snapshot), entry)
            : [
                {
                  label: words.settingsVersion,
                  theirs: String(snapshot.settings_version),
                },
              ]) as Conflict["facts"],
          keepMine: {
            label: words.keepMine,
            run: () => {
              keep.keepMine();
              setConflicted(false);
              setNeededVersion(null);
              outcome.reset();
            },
          },
          loadNewer: {
            label: words.loadTheirs,
            run: () => {
              keep.loadNewer();
              setConflicted(false);
              setNeededVersion(null);
              outcome.reset();
            },
          },
        }
      : undefined;
  const blockers = (): Blocker[] => [
    ...(conflicted
      ? [
          {
            kind: "conflict" as const,
            part: conflictPart,
            sentence: conflict
              ? words.chooseWhichSettingsToKeep
              : words.refreshBeforeChoosingSettings,
          },
        ]
      : []),
    ...(invalid
      ? [
          {
            kind: "missing" as const,
            part: "locations",
            sentence: words.useAtMost50LocationsEachUpTo200Characters,
          },
        ]
      : []),
  ];
  const save = (key: string) => {
    const sent = {
      expected_version: expectedVersion!,
      enabled_meeting_methods: enabled,
      default_meeting_method: defaultMethod,
      locations,
      reason: reasonText.trim() || null,
    };
    return outcome.run(
      key,
      async (id) => {
        try {
          const next = await settingsApi.updateCourseDelivery(sent, id);
          queryClient.setQueryData(["admin", "settings"], next);
          setConflicted(false);
          setNeededVersion(null);
          void queryClient.invalidateQueries({ queryKey: ["tenant", "me"] });
          return next.committed_version === undefined ? undefined : String(next.committed_version);
        } catch (error) {
          const current = currentOnConflict<CourseDeliveryConflict>(error);
          if (current) {
            setNeededVersion(current.settings_version);
            setConflicted(true);
            await settings.refetch();
          }
          throw error;
        }
      },
      [words.courseDeliverySavedNewCoursesUseTheseSettings],
      refresh,
    );
  };
  const partState = (part: string) => ({
    class: changed(part) ? ("draft" as const) : ("done" as const),
    word: changed(part) ? words.changed : words.asSaved,
  });
  const boxes: Box[] = [
    {
      id: "course-delivery",
      readTime: true,
      job: words.chooseCourseDeliveryMethodsAndLocations,
      kind: "task",
      label: copy.delivery,
      owner: imam,
      read,
      fold: "never",
      detail: () => words.chooseMethodsStaffMaySelectAndTheDefaultForNewCourses,
      state: () => ({
        class: outcome.result.state === "unknown" ? "reading" : edited ? "draft" : "done",
        word: outcome.result.state === "unknown" ? words.notConfirmed : edited ? words.changesNotSaved : words.courseDeliverySet,
      }),
      draft: keep.kept,
      result: outcome.result,
      candidate: { eligible: true, blocked: null, rank: null },
      parts: [
        {
          id: "methods",
          label: words.meetingMethods,
          owner: imam,
          state: partState("methods"),
          fold: "never",
          draft: "browser",
          keep,
          conflict: conflictPart === "methods" ? conflict : undefined,
          markedByBox: !changed("methods") || undefined,
          render: () => (
            <div className="grid gap-2">
              {METHODS.map((method) => {
                const last = enabled.length === 1 && enabled.includes(method);
                return (
                  <div
                    key={method}
                    className="grid gap-1 border-t border-rule py-3"
                  >
                    <label className="flex items-center justify-between gap-3">
                      <span>
                        {LABEL[method]}{" "}
                        <small className="text-muted-foreground">
                          <span data-figure="">
                            {snapshot?.course_delivery.usage_counts[method]}
                          </span>{" "}
                          {words.coursesUseIt}
                        </small>
                      </span>
                      <Switch
                        aria-label={settingsText.enableMethod(method)}
                        checked={enabled.includes(method)}
                        disabled={
                          last ||
                          outcome.pending ||
                          outcome.result.state === "unknown"
                        }
                        aria-describedby={
                          last ? "last-method-reason" : undefined
                        }
                        onCheckedChange={(checked) =>
                          change(() => {
                            const next = checked
                              ? METHODS.filter(
                                  (value) =>
                                    value === method || enabled.includes(value),
                                )
                              : enabled.filter((value) => value !== method);
                            setEnabled(next);
                            if (!next.includes(defaultMethod))
                              setDefaultMethod(next[0]);
                          })
                        }
                      />
                    </label>
                    {last && (
                      <p
                        id="last-method-reason"
                        className="text-meta text-muted-foreground"
                      >
                        {copy.lastMethod}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ),
        },
        {
          id: "default",
          markedByBox: !changed("default") || undefined,
          conflict: conflictPart === "default" ? conflict : undefined,
          label: words.defaultMeetingMethod,
          owner: imam,
          state: partState("default"),
          fold: "never",
          draft: "browser",
          keep,
          render: () => (
            <>
              <Label className="sr-only" htmlFor="default-meeting">
                {words.defaultMeetingMethod}
              </Label>
              <NativeSelect
                disabled={outcome.pending || outcome.result.state === "unknown"}
                id="default-meeting"
                value={defaultMethod}
                onChange={(event) =>
                  change(() =>
                    setDefaultMethod(
                      event.target.value as ConfigurableMeetingMethod,
                    ),
                  )
                }
              >
                {enabled.map((method) => (
                  <option key={method} value={method}>
                    {LABEL[method]}
                  </option>
                ))}
              </NativeSelect>
            </>
          ),
        },
        {
          id: "locations",
          markedByBox: !changed("locations") || undefined,
          conflict: conflictPart === "locations" ? conflict : undefined,
          label: words.locations,
          owner: imam,
          state: partState("locations"),
          fold: "never",
          draft: "browser",
          keep,
          render: () => (
            <>
              <Label className="sr-only" htmlFor="meeting-locations">
                {words.locations}
              </Label>
              <Textarea
                disabled={outcome.pending || outcome.result.state === "unknown"}
                id="meeting-locations"
                value={locationsText}
                aria-invalid={invalid}
                aria-describedby="location-help"
                onChange={(event) =>
                  change(() => setLocationsText(event.target.value))
                }
              />
              <p id="location-help" className="text-meta text-muted-foreground">
                {invalid
                  ? words.useAtMost50LocationsEachUpTo200Characters
                  : words.oneLocationPerLineBlankLinesAreIgnored}
              </p>
            </>
          ),
        },
        {
          id: "reason",
          label: words.reason,
          owner: imam,
          state: { class: "quiet", word: words.optional },
          markedByBox: true,
          fold: "never",
          draft: "browser",
          keep,
          render: () => (
            <>
              <Label className="sr-only" htmlFor="delivery-reason">
                {words.reasonForThisChangeOptional}
              </Label>
              <Textarea
                id="delivery-reason"
                maxLength={500}
                value={reasonText}
                disabled={outcome.pending || outcome.result.state === "unknown"}
                onChange={(event) =>
                  change(() => setReasonText(event.target.value))
                }
              />
              <p className="text-meta text-muted-foreground">
                {words.keptInChangeHistoryWithThisSave}
              </p>
            </>
          ),
        },
      ],
      commit: {
        verb: words.saveCourseDelivery,
        cap: words.whenYouSaveCourseDelivery,
        effects: () => settingsText.deliveryEffects(saved, entry),
        blockers,
        revises: { part: "methods", unchanged: () => !edited },
        run: save,
      },
    },
    {
      id: "managed-rooms",
      job: words.readManagedMeetingRoomAvailability,
      kind: "fact",
      label: copy.rooms,
      owner: imam,
      read: boxRead(settings, copy.roomsUnavailable),
      fold: "never",
      detail: () => words.providersOwnManagedMeetingRoomsManualLinksRemainValid,
      prose: (value) => settingsText.roomAvailability(
        (value as InstitutionSettings).course_delivery.managed_meeting_creation,
      ),
      body: () => (
        <p className="text-ui text-muted-foreground">
          {words.providersOwnManagedMeetingRoomsManualLinksRemainValid}
        </p>
      ),
    },
    {
      id: "settings-history",
      job: words.readCourseDeliveryChanges,
      kind: "list",
      label: copy.history,
      owner: imam,
      read:
        snapshot && !snapshot.recent_changes.length && read.status === "ready"
          ? { status: "empty", sentence: words.noChangesRecordedYet }
          : boxRead(settings, copy.historyUnavailable),
      fold: "never",
      detail: () => words.courseDeliveryChangesNewestFirst,
      state: () => ({ class: "quiet", word: words.newestFirst }),
      figures: (value) => [
        {
          value: (value as InstitutionSettings).recent_changes.length,
          unit: settingsText.historyUnit((value as InstitutionSettings).recent_changes.length),
        },
      ],
      rows: (value) => (value as InstitutionSettings).recent_changes,
      row: {
        key: (row) => (row as SettingsChange).id,
        title: (row) => {
          const item = row as SettingsChange;
          const { lines, recognised } = describeChange(item);
          return (
            lines.join(". ") ||
            (recognised
              ? words.savedWithNoChange
              : words.thisChangeUsesAnOlderRecordFormat)
          );
        },
        facts: (row) => {
          const item = row as SettingsChange;
          return settingsText.historyFacts(
            item.created_at,
            me?.user_id === item.actor_user_id,
            item.reason,
          );
        },
      },
    },
  ];
  if (settings.error instanceof ApiError && settings.error.status === 403)
    return (
      <PageRefusal
        id="settings"
        journey="settings"
        refusal={{
          reason: "forbidden",
          sentence: copy.forbidden,
          back: { label: words.goBack, to: "/admin" },
        }}
      />
    );
  return (
    <div className="app-page">
      <AcademicPageHeader
        eyebrow={eyebrow}
        title={copy.title}
        description={copy.description}
      />
      <Link to="/admin/settings/integrations" className="text-ui font-semibold text-primary">{integrationWords.title}</Link>
      <Bento
        id="settings"
        journey="settings"
        onOpenChange={(id, open) => {
          if (open && id === signinBox.id) signinBox.onOpen();
        }}
        boxes={[
          ...boxes,
          { ...gradeScale, readTime: true },
          ...(zoomEnabled
            ? [signinBox, ...receiverBoxes, verificationBox]
            : []),
        ]}
      />
    </div>
  );
}
